/**
 * Rob Steins cart helpers.
 *
 *  <rs-addon>            An on/off switch for an optional product (shipping protection, gift packaging).
 *                        Rendered by snippets/rs-cart-extras.liquid inside the cart drawer and the cart page.
 *  [data-rs-add-variant] Any button with this attribute adds that variant once ("+ Add" in the upsell row).
 *
 * Both talk to the cart through Horizon's standard events and Section Rendering, so the drawer, the cart page
 * and the cart count refresh exactly as they do for the theme's own buttons.
 *
 * Add-ons can be pre-selected (Theme settings > Rob Steins > Cart extras): the server renders the switch on and
 * marks it data-auto, and the add-on is added to the bag the first time the cart is actually on screen. Switching
 * it off removes it and stores the choice in a cart attribute (data-optout), so it stays off from then on.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/** Section ids of every cart-items component on the page (drawer and/or cart page). */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
}

/** Cart changes run one at a time so two switches never race each other. */
let queue = Promise.resolve();
/**
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function enqueue(task) {
  const run = queue.then(task, task);
  queue = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

const fetchCart = async () => (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();

/** @param {string} name @param {string} value  An empty value deletes the attribute. */
const setCartAttribute = (name, value) =>
  fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { [name]: value } }),
  }).catch(() => undefined);

/**
 * @param {EventTarget} source
 * @param {{ action: 'add'|'remove', lines: any[], url: string, body: Record<string, unknown> }} request
 */
async function mutateCart(source, { action, lines, url, body }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({ action, context: action === 'add' ? 'product' : 'cart', lines, promise: deferred.promise })
  );

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: cartSectionIds() }),
    });
    const json = await response.json();

    if (json.status || json.errors) {
      source.dispatchEvent(
        new CartErrorEvent({
          error: json.message || json.errors || 'Cart update failed',
          code: 'INVALID',
          detail: { description: json.description, errors: json.errors },
        })
      );
      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'rs-cart', sourceId: 'rs-cart', itemCount: 0 },
      });
      return false;
    }

    // add.js returns the added line; the full cart is needed for the standard event payload.
    const cart = action === 'add' ? await fetchCart() : json;
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'rs-cart',
        sourceId: 'rs-cart',
        itemCount: cart.item_count,
        sections: json.sections,
        didError: false,
      },
    });
    return true;
  } catch (error) {
    console.error(error);
    deferred.reject(error);
    source.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error updating the cart', code: 'SERVICE_UNAVAILABLE' })
    );
    return false;
  }
}

/** @param {EventTarget} source @param {number} variantId @param {number} quantity */
const addVariant = (source, variantId, quantity = 1) =>
  mutateCart(source, {
    action: 'add',
    lines: [{ merchandiseId: String(variantId), quantity }],
    url: Theme.routes.cart_add_url,
    body: { items: [{ id: variantId, quantity }] },
  });

/**
 * Removes the line for a variant (looked up in the live cart) and, when given, records the opt-out attribute in the same request.
 * @param {EventTarget} source @param {number} variantId @param {string} [optOut]
 */
async function removeVariant(source, variantId, optOut) {
  const cart = await fetchCart();
  const line = cart.items.find((item) => item.variant_id === variantId);
  if (!line) {
    if (optOut) await setCartAttribute(optOut, '1');
    return true;
  }
  return mutateCart(source, {
    action: 'remove',
    lines: [{ id: line.key, quantity: 0 }],
    url: Theme.routes.cart_update_url,
    body: { updates: { [line.key]: 0 }, ...(optOut ? { attributes: { [optOut]: '1' } } : {}) },
  });
}

/** Variants currently being added automatically, so a re-render cannot trigger the same add twice. */
const autoInFlight = new Set();

/**
 * Wraps or unwraps every bracelet on the page (and lights the gift row) straight away, without waiting for the cart to answer,
 * so switching gift packaging feels instant. The cart's own re-render arrives a moment later with the same state and changes nothing.
 * @param {boolean} on
 */
function setGiftLook(on) {
  for (const story of document.querySelectorAll('.rs-story')) {
    // only bracelets that carry the gift layers (the cart's), not the product page's
    if (!story.querySelector('.rs-story__paper')) continue;
    if (on) {
      story.classList.remove('rs-story--unwrap');
      story.classList.add('rs-story--gift');
    } else if (story.classList.contains('rs-story--gift')) {
      story.classList.remove('rs-story--gift');
      story.classList.add('rs-story--unwrap');
      setTimeout(() => story.classList.remove('rs-story--unwrap'), 500);
    }
  }
  for (const row of document.querySelectorAll('.rs-giftrow')) row.classList.toggle('is-on', on);
}

/**
 * Attributes:
 *  data-variant-id  the variant to add
 *  data-line-key    present while the product is in the cart
 *  data-optout      name of the cart attribute that remembers "switched off"
 *  data-auto        add it as soon as the cart is on screen (pre-selected)
 *  data-orphan      it is in the cart but no real product is: remove it
 *  data-gift        this is the gift packaging switch: the bracelet is wrapped or unwrapped instantly (see setGiftLook)
 * The switch is server-rendered on/off; after every cart update Horizon morphs a fresh copy in.
 */
class RsAddon extends HTMLElement {
  #observer = /** @type {MutationObserver | null} */ (null);

  connectedCallback() {
    this.addEventListener('change', this.#onChange);

    const variantId = Number(this.dataset.variantId);
    if (this.hasAttribute('data-orphan')) {
      enqueue(() => removeVariant(this, variantId));
    } else if (this.hasAttribute('data-auto') && !autoInFlight.has(variantId)) {
      // Only once the cart is actually open (the drawer's dialog), or straight away on the cart page.
      // (Not "when scrolled into view": the switches can sit below the fold of the drawer.)
      const dialog = this.closest('dialog');
      if (!dialog || dialog.open) {
        this.#autoAdd(variantId);
      } else {
        this.#observer = new MutationObserver(() => {
          if (!dialog.open) return;
          this.#observer?.disconnect();
          this.#autoAdd(variantId);
        });
        this.#observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.#observer?.disconnect();
  }

  /** @param {number} variantId */
  async #autoAdd(variantId) {
    if (autoInFlight.has(variantId)) return;
    autoInFlight.add(variantId);
    const input = this.querySelector('input');
    if (input instanceof HTMLInputElement) input.disabled = true;
    const ok = await enqueue(() => addVariant(this, variantId));
    // If it could not be added, show the switch as off instead of pretending.
    if (!ok && input instanceof HTMLInputElement) {
      input.checked = false;
      input.disabled = false;
    }
    setTimeout(() => autoInFlight.delete(variantId), 4000);
  }

  /** @param {Event} event */
  #onChange = async (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;

    input.disabled = true;
    const wantOn = input.checked;
    const variantId = Number(this.dataset.variantId);
    const optOut = this.dataset.optout || '';

    // the bracelet reacts at once; it is put back below if the cart says no
    const isGift = this.hasAttribute('data-gift');
    if (isGift) setGiftLook(wantOn);

    let ok;
    if (wantOn) {
      if (optOut) await setCartAttribute(optOut, '');
      ok = await enqueue(() => addVariant(this, variantId));
    } else {
      ok = await enqueue(() => removeVariant(this, variantId, optOut));
    }

    // On success Horizon re-renders this element; on failure put the switch back where it was.
    if (!ok) {
      input.checked = !wantOn;
      if (isGift) setGiftLook(!wantOn);
    }
    input.disabled = false;
  };
}

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-rs-add-variant]') : null;
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const label = button.querySelector('span') || button;
  const original = label.textContent;
  button.disabled = true;
  label.textContent = 'Adding…';
  const ok = await enqueue(() => addVariant(button, Number(button.dataset.rsAddVariant)));
  label.textContent = ok ? 'Added' : original;
  setTimeout(() => {
    button.disabled = false;
    label.textContent = original;
  }, 1200);
});

if (!customElements.get('rs-addon')) customElements.define('rs-addon', RsAddon);
